<template>
    <div class="main-layout__container">
        <div class="content__header content__header--one-item-per-line">
            <div class="content-header__back">
                <UIBackButton
                    :text="'К списку пользователей'"
                    :to="router.resolve({ name: 'HandbookUsers' }).href"
                />
            </div>

            <div class="content-header__title">Добавление пользователя</div>

            <div class="content-header__background content-header__background--add-edit-page"></div>
        </div>

        <div class="content__body">
            <div class="form__content">
                <div class="form__fields">
                    <div class="form__field-group">
                        <UISelect
                            id="dealer_id"
                            v-model="form.dealer_id"
                            :options="dealerOptions"
                            label="Дилер"
                            :error-message="errors?.dealer_id?.join() ?? ''"
                            searchable
                        />

                        <UIInput
                            id="email"
                            v-model="form.email"
                            label="Email*"
                            :error-message="errors?.email?.join() ?? ''"
                        />

                        <UIInput
                            id="phone"
                            v-model="form.phone"
                            label="Телефон"
                            :error-message="errors?.phone?.join() ?? ''"
                        />
                    </div>

                    <div class="form__field-group">
                        <UIInput
                            id="first_name"
                            v-model="form.first_name"
                            label="Имя*"
                            :error-message="errors?.first_name?.join() ?? ''"
                        />

                        <UIInput
                            id="last_name"
                            v-model="form.last_name"
                            label="Фамилия*"
                            :error-message="errors?.last_name?.join() ?? ''"
                        />

                        <UIInput
                            id="second_name"
                            v-model="form.second_name"
                            label="Отчество"
                            :error-message="errors?.second_name?.join() ?? ''"
                        />
                    </div>

                    <div class="form__field-group">
                        <UIInput
                            id="position"
                            v-model="form.position"
                            label="Должность"
                            :error-message="errors?.position?.join() ?? ''"
                        />

                        <UIInput
                            id="password"
                            v-model="form.password"
                            label="Пароль*"
                            :error-message="errors?.password?.join() ?? ''"
                        />

                        <UIInput
                            id="password_confirmation"
                            v-model="form.password_confirmation"
                            label="Подтверждение пароля*"
                            :error-message="errors?.password?.join() ?? ''"
                        />

                        <UISelect
                            id="is_admin"
                            v-model="form.is_admin"
                            :options="roleOptions"
                            label="Роль*"
                            :error-message="errors?.is_admin?.join() ?? ''"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="content__footer">
            <div class="form__actions">
                <UISimpleButton
                    button-type="outlined"
                    @clicked="goToUsersPage"
                >
                    Выйти без сохранения
                </UISimpleButton>

                <UISimpleButton
                    :loading="saveLoading"
                    @clicked="saveUser"
                >
                    Сохранить
                </UISimpleButton>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { onMounted, ref } from 'vue';
    import { useRouter } from 'vue-router';

    import { SelectOption } from '@/types/form.type.ts';
    import { DealerListItem } from '@/types/dealer.type.ts';
    import { UserCreateForm, UserCreateFormErrors } from '@/types/user.type.ts';

    import useDealerRepository from '@/repositories/dealer/useDealerRepository.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import useUserRepository from '@/repositories/user/useUserRepository.ts';

    import UIBackButton from '@/components/ui/UIBackButton/UIBackButton.vue';
    import UIInput from '@/components/ui/UIInput/UIInput.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';

    const router = useRouter();
    const { createUser } = useUserRepository();
    const { getAllActiveDealers } = useDealerRepository();
    const { showSuccess } = useNotifications();

    const form = ref<UserCreateForm>({
        dealer_id: undefined,
        email: '',
        last_name: '',
        first_name: '',
        second_name: '',
        password: '',
        password_confirmation: '',
        is_admin: 0,
        position: '',
        phone: '',
    });
    const dealerOptions = ref<SelectOption[]>([]);
    const roleOptions = ref<SelectOption[]>([
        { label: 'Администратор', value: 1 },
        { label: 'Пользователь', value: 0 },
    ]);
    const saveLoading = ref<boolean>(false);
    const errors = ref<UserCreateFormErrors | null>(null);

    function saveUser(): void {
        saveLoading.value = true;
        createUser(form.value)
            .then(() => {
                showSuccess({
                    title: 'Пользователь добавлен',
                });

                goToUsersPage();
            })
            .catch((createErrors: UserCreateFormErrors) => {
                errors.value = createErrors;
            })
            .finally(() => {
                saveLoading.value = false;
            });
    }

    function goToUsersPage(): void {
        router.push({ name: 'HandbookUsers' });
    }

    function fetchFormOptions(): void {
        getAllActiveDealers().then((dealers: DealerListItem[]) => {
            const options: SelectOption[] = [];
            dealers.forEach((dealer: DealerListItem) => {
                options.push({
                    label: dealer.name,
                    value: dealer.id,
                });
            });

            dealerOptions.value = options;
        });
    }

    onMounted(() => {
        fetchFormOptions();
    });
</script>
